import { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Bot, CheckCircle2, XCircle, Trash2, Save, Loader2, ShieldCheck, PlugZap,
  Activity, AlertTriangle, Zap, Minus, RefreshCcw,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';


const STATUS_META = {
  healthy:         { label: 'Healthy',        color: '#22c55e', icon: CheckCircle2 },
  degraded:        { label: 'Degraded',       color: '#f59e0b', icon: AlertTriangle },
  failing:         { label: 'Failing',        color: '#ef4444', icon: XCircle },
  idle:            { label: 'Idle (no sends)', color: '#64748b', icon: Minus },
  disabled:        { label: 'Disabled',       color: '#64748b', icon: Minus },
  no_recipients:   { label: 'No recipients',  color: '#f59e0b', icon: AlertTriangle },
  not_configured:  { label: 'Not configured', color: '#64748b', icon: Minus },
};

function HealthCard({ h }) {
  const meta = STATUS_META[h.status] || STATUS_META.not_configured;
  const Icon = meta.icon;
  const bot = BOT_TYPE_META[h.bot_type] || { title: h.bot_type, accent: '#64748b' };
  return (
    <div
      className="rounded-xl border p-4 bg-[#131822]"
      style={{ borderColor: `${meta.color}33` }}
      data-testid={`bot-health-${h.bot_type}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-white">
          <Bot className="w-4 h-4" style={{ color: bot.accent }} />
          {bot.title}
        </div>
        <span
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded-full border"
          style={{ color: meta.color, borderColor: `${meta.color}66`, background: `${meta.color}15` }}
        >
          <Icon className="w-3 h-3" /> {meta.label}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center mt-2">
        <div>
          <div className="text-xl font-bold text-white">{h.sent_24h ?? 0}</div>
          <div className="text-[10px] text-gray-500 uppercase">Sent 24h</div>
        </div>
        <div>
          <div className="text-xl font-bold text-green-400">{h.ok_24h ?? 0}</div>
          <div className="text-[10px] text-gray-500 uppercase">OK</div>
        </div>
        <div>
          <div className="text-xl font-bold text-red-400">{h.error_24h ?? 0}</div>
          <div className="text-[10px] text-gray-500 uppercase">Errors</div>
        </div>
      </div>
      <div className="mt-3 pt-2 border-t border-white/5 space-y-1 text-xs text-gray-400">
        <div className="flex justify-between">
          <span>Success rate</span>
          <span className="text-gray-200">
            {h.success_rate != null ? `${h.success_rate}%` : '—'}
          </span>
        </div>
        <div className="flex justify-between">
          <span>Last send</span>
          <span className="text-gray-200 truncate max-w-[140px]" title={h.last_message_at || ''}>
            {h.last_message_at ? new Date(h.last_message_at).toLocaleString() : '—'}
          </span>
        </div>
        <div className="flex justify-between">
          <span>Recipients</span>
          <span className="text-gray-200">{h.chat_ids_count}</span>
        </div>
        {h.last_error && (
          <div
            className="mt-2 text-[11px] text-red-300 bg-red-500/10 border border-red-500/25 rounded px-2 py-1.5 truncate"
            title={h.last_error}
            data-testid={`bot-health-error-${h.bot_type}`}
          >
            ⚠ {h.last_error}
          </div>
        )}
      </div>
    </div>
  );
}

const BOT_TYPE_META = {
  cash_in: {
    title: 'Cash In Group',
    description: 'Receives all NON-Chime manual deposit requests with screenshot + player info.',
    accent: '#22c55e',
  },
  cash_out: {
    title: 'Cash Out Group',
    description: 'Receives all withdrawal requests with player info + payout QR.',
    accent: '#f59e0b',
  },
  chime_in: {
    title: 'Chime In Group',
    description: 'Receives deposits for payment methods whose NAME contains "chime".',
    accent: '#8b5cf6',
  },
};

function BotCard({ bot, scope, onSaved, onDeleted }) {
  const meta = BOT_TYPE_META[bot.bot_type];
  const [token, setToken] = useState('');
  const [chatIds, setChatIds] = useState((bot.whitelisted_chat_ids || []).join('\n'));
  const [enabled, setEnabled] = useState(bot.enabled ?? true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setChatIds((bot.whitelisted_chat_ids || []).join('\n'));
    setEnabled(bot.enabled ?? true);
    setTestResult(null);
    setToken('');
  }, [bot.id, bot.updated_at]);

  const chatList = chatIds
    .split(/[,\n]/).map((s) => s.trim()).filter(Boolean);

  const testConnection = async () => {
    if (!token.trim()) { toast.error('Paste the bot token from BotFather first'); return; }
    setTesting(true); setTestResult(null);
    try {
      const res = await api.post('/api/admin/bot-approvals/bots/test', { bot_token: token.trim() });
      setTestResult(res.data);
      if (res.data?.ok) {
        toast.success(`Connected as @${res.data.bot_username || 'bot'}`);
      } else {
        toast.error(res.data?.error || 'Telegram rejected this token');
      }
    } catch (e) {
      const err = e.response?.data?.error || e.response?.data?.detail || e.message;
      setTestResult({ ok: false, error: err });
      toast.error(err);
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    if (!token.trim() && !bot.configured) {
      toast.error('Enter the bot token before saving');
      return;
    }
    if (chatList.length === 0) {
      toast.error('At least one Telegram chat/user id is required');
      return;
    }
    setSaving(true);
    try {
      // If the admin is editing an already-configured bot (only updating
      // chat ids or the enabled toggle), skip the token — the backend
      // will reuse the stored one instead of forcing the admin to paste
      // it again (Telegram tokens are masked in our UI for security).
      const payload = {
        bot_type: bot.bot_type,
        whitelisted_chat_ids: chatList,
        enabled,
      };
      if (token.trim()) {
        payload.bot_token = token.trim();
      }
      const res = await api.post('/api/admin/bot-approvals/bots', payload);
      toast.success('Bot saved');
      setToken('');
      onSaved?.(res.data?.bot);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save bot');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!bot.id) return;
    if (!confirm(`Remove ${meta.title}? This will also unregister its Telegram webhook.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/api/admin/bot-approvals/bots/${bot.id}`);
      toast.success('Bot removed');
      onDeleted?.(bot.bot_type);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete bot');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className="rounded-2xl border bg-[#131822] p-6"
      style={{ borderColor: `${meta.accent}33` }}
      data-testid={`bot-card-${bot.bot_type}`}
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center"
            style={{ background: `${meta.accent}22`, color: meta.accent }}
          >
            <Bot className="w-6 h-6" />
          </div>
          <div>
            <div className="text-white font-bold text-lg">{meta.title}</div>
            <div className="text-xs text-gray-400 max-w-md">{meta.description}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {bot.configured ? (
            <span className="inline-flex items-center gap-1 text-xs text-green-400 bg-green-500/15 border border-green-500/30 rounded-full px-2 py-0.5">
              <CheckCircle2 className="w-3 h-3" /> configured
              {bot.bot_username ? <span className="text-green-300/80">· @{bot.bot_username}</span> : null}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs text-gray-400 bg-white/5 border border-white/10 rounded-full px-2 py-0.5">
              not configured
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Bot Token (from @BotFather)</label>
            <Input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder={bot.configured ? `${bot.bot_token_masked || '••••••'} (paste again to update)` : '123456:ABC-DEF…'}
              data-testid={`bot-token-${bot.bot_type}`}
              className="bg-[#0B0E14] border-white/10 text-white"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={testConnection}
              disabled={testing || !token.trim()}
              data-testid={`bot-test-${bot.bot_type}`}
              className="flex-1"
            >
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlugZap className="w-4 h-4 mr-2" />}
              Test Connection
            </Button>
          </div>
          {testResult && (
            <div
              className={`text-xs rounded-lg px-3 py-2 border ${
                testResult.ok
                  ? 'bg-green-500/10 border-green-500/30 text-green-400'
                  : 'bg-red-500/10 border-red-500/30 text-red-400'
              }`}
              data-testid={`bot-test-result-${bot.bot_type}`}
            >
              {testResult.ok ? (
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Connected as <b>@{testResult.bot_username}</b> (id {testResult.bot_id})
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <XCircle className="w-3.5 h-3.5" />
                  {testResult.error}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">
              Authorized Telegram Chat / User IDs
              <span className="text-gray-500 ml-2">(one per line or comma-separated — only these can Approve/Reject)</span>
            </label>
            <Textarea
              rows={4}
              value={chatIds}
              onChange={(e) => setChatIds(e.target.value)}
              placeholder={'123456789\n-1001234567890'}
              data-testid={`bot-chat-ids-${bot.bot_type}`}
              className="bg-[#0B0E14] border-white/10 text-white font-mono text-sm"
            />
            <div className="text-xs text-gray-500 mt-1">
              {chatList.length} chat id{chatList.length === 1 ? '' : 's'} will receive messages.
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-gray-300">
            <Switch checked={!!enabled} onCheckedChange={setEnabled} data-testid={`bot-enabled-${bot.bot_type}`} />
            Enabled
          </label>
        </div>
      </div>

      <div className="flex items-center justify-between mt-5 pt-4 border-t border-white/5">
        <div className="text-xs text-gray-500 flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5" />
          Approvals from this bot are logged in Staff Activity.
        </div>
        <div className="flex gap-2">
          {bot.configured && (
            <Button variant="outline" onClick={remove} disabled={deleting} data-testid={`bot-delete-${bot.bot_type}`}>
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Remove
            </Button>
          )}
          <Button
            onClick={save}
            disabled={saving}
            data-testid={`bot-save-${bot.bot_type}`}
            style={{ background: meta.accent, color: '#0B0E14' }}
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function ApprovalBotsPage() {
  const [bots, setBots] = useState([]);
  const [scope, setScope] = useState(null);
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState([]);
  const [healthLoading, setHealthLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/bot-approvals/bots');
      setBots(res.data?.bots || []);
      setScope(res.data?.scope || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load bot config');
    } finally {
      setLoading(false);
    }
  };

  const loadHealth = async () => {
    setHealthLoading(true);
    try {
      const r = await api.get('/api/admin/bot-approvals/health');
      setHealth(r.data?.health || []);
    } catch {
      // silent — health is supplementary
    } finally {
      setHealthLoading(false);
    }
  };

  useEffect(() => { load(); loadHealth(); }, []);
  // Refresh health every 30s when the tab is focused
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') loadHealth();
    }, 30000);
    return () => clearInterval(id);
  }, []);

  const onBotSaved = () => { load(); loadHealth(); };
  const onBotDeleted = () => { load(); loadHealth(); };

  const scopeLabel = scope?.scope_type === 'main'
    ? 'Main (Master Platform)'
    : `Tenant (${scope?.scope_id?.slice(0, 8) || ''}…)`;

  return (
    <div className="space-y-6" data-testid="approval-bots-page">
      <div>
        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
          <Bot className="w-7 h-7 text-emerald-400" />
          Payment Approval Bots
        </h1>
        <p className="text-sm text-gray-400 mt-1">
          Configure the 3 Telegram groups that receive your <b>manual</b> Cash In, Chime In and Cash Out approvals.
          Approvers press the buttons in Telegram — decisions are applied instantly.
        </p>
        <p className="text-xs text-gray-500 mt-1">Scope: <b className="text-gray-300">{scopeLabel}</b></p>
      </div>

      {loading ? (
        <div className="text-gray-400 text-sm flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading bot configuration…
        </div>
      ) : (
        <>
          {/* Bot Health Widget */}
          <div className="rounded-2xl border border-white/10 bg-[#0d1119]/60 p-5" data-testid="bot-health-widget">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-emerald-400" />
                <div className="text-white font-semibold">Bot Health (last 24 h)</div>
                {healthLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-500" /> : null}
              </div>
              <Button variant="outline" size="sm" onClick={loadHealth} data-testid="bot-health-refresh">
                <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Refresh
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {health.length === 0 && !healthLoading ? (
                <div className="col-span-3 text-gray-500 text-sm text-center py-6">
                  No telemetry yet — it will appear here as soon as requests start flowing to your bots.
                </div>
              ) : (
                health.map((h) => <HealthCard key={h.bot_type} h={h} />)
              )}
            </div>
          </div>

          <div className="space-y-5">
            {bots.map((b) => (
              <BotCard
                key={b.bot_type}
                bot={b}
                scope={scope}
                onSaved={onBotSaved}
                onDeleted={onBotDeleted}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
